// Message validation service for chat protection
export interface ValidationResult {
  isValid: boolean;
  reason?: string;
  sanitizedMessage?: string;
}

interface ValidationLimits {
  maxLength: number;
  blockedPatterns: RegExp[];
  spamPatterns: RegExp[];
}

class MessageValidationService {
  private limits: ValidationLimits;

  constructor() {
    this.limits = {
      maxLength: parseInt(import.meta.env.VITE_CHAT_MAX_MESSAGE_LENGTH) || 500,
      blockedPatterns: [
        // Only block very specific prompt injection attempts
        /ignore.*previous.*instructions/i,
        /forget.*everything.*above/i,
        /act.*as.*different.*ai/i,
        /pretend.*you.*are.*not/i,
        
        // Block attempts to extract system info
        /what.*are.*your.*system.*instructions/i,
        /show.*me.*your.*full.*prompt/i,
        /reveal.*your.*system.*prompt/i
      ],
      spamPatterns: [
        // Only very obvious spam patterns
        // Repeated characters (more than 15 in a row - very excessive)
        /(.)\1{15,}/,
        
        // Excessive punctuation (more than 10 in a row)
        /[!?]{10,}/,
        
        // All caps for entire long messages (more than 50 characters all caps)
        /^[A-Z\s!?.,]{50,}$/,
        
        // Excessive whitespace (more than 20 spaces)
        /\s{20,}/
      ]
    };
  }

  validateMessage(message: string): ValidationResult {
    // Check message length
    if (message.length > this.limits.maxLength) {
      return {
        isValid: false,
        reason: `Message too long. Maximum ${this.limits.maxLength} characters allowed.`
      };
    }

    // Check for empty or whitespace-only messages
    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      return {
        isValid: false,
        reason: 'Message cannot be empty.'
      };
    }

    // Check for blocked patterns (only very specific ones)
    for (const pattern of this.limits.blockedPatterns) {
      if (pattern.test(message)) {
        return {
          isValid: false,
          reason: 'This type of request is not supported. Please ask about our business services instead.'
        };
      }
    }

    // Check for spam patterns (only very obvious ones)
    for (const pattern of this.limits.spamPatterns) {
      if (pattern.test(message)) {
        return {
          isValid: false,
          reason: 'Message appears to contain excessive repetition or formatting.'
        };
      }
    }

    // Basic sanitization
    const sanitizedMessage = this.sanitizeMessage(trimmedMessage);

    return {
      isValid: true,
      sanitizedMessage
    };
  }

  private sanitizeMessage(message: string): string {
    // Remove excessive whitespace
    let sanitized = message.replace(/\s+/g, ' ');
    
    // Remove potential HTML/script tags (basic protection)
    sanitized = sanitized.replace(/<[^>]*>/g, '');
    
    // Limit consecutive punctuation (only very excessive)
    sanitized = sanitized.replace(/([!?.]){6,}/g, '$1$1$1');
    
    return sanitized.trim();
  }

  // Get current limits (useful for displaying to user)
  getLimits(): { maxLength: number } {
    return {
      maxLength: this.limits.maxLength
    };
  }

  // Check if message might be suspicious (for triggering CAPTCHA) - much more lenient
  isSuspicious(message: string): boolean {
    const suspiciousPatterns = [
      // Very short repetitive messages (like "aaaa" repeated)
      /^(.{1,2})\1{5,}$/,
      
      // Messages with only numbers and very long
      /^\d{20,}$/,
      
      // Messages with excessive special characters (more than 20)
      /[^a-zA-Z0-9\s]{20,}/,
      
      // Very long words (potential gibberish - more than 40 characters)
      /\b\w{40,}\b/
    ];

    return suspiciousPatterns.some(pattern => pattern.test(message));
  }
}

// Export singleton instance
export const messageValidationService = new MessageValidationService();
